import { HTTPError } from 'ky';
import { http } from '@/lib/http';

/**
 * The e-ticket of a confirmed flight purchase, read live from the airline order through the
 * BFF (`/v1/flight-tickets`), and its delivery to the passenger. A standalone reservation is
 * keyed by its request id (`direct-flight`); a replacement purchase inside a disruption by
 * its purchase id (`lcc-purchase`). No passenger contact ever reaches the console.
 */
export type FlightTicketKind = 'direct-flight' | 'lcc-purchase';

export interface TicketMoney {
  amount: string;
  currency: string;
}
export interface TicketCondition {
  allowed: boolean;
  /** The change / refund fee, priced by the BFF — `null` without a price card (D17). Present
   *  (priced or null) means «with a fee»; absent means none. The sheet never prints it. */
  penalty?: TicketMoney | null;
}
export interface TicketSegment {
  flightNumber: string;
  marketingAirline: string;
  operatingAirline: string;
  operatingFlightNumber?: string;
  origin: string;
  destination: string;
  departureLocal: string;
  arrivalLocal: string;
  originName?: string;
  originCity?: string;
  originTerminal?: string;
  destinationName?: string;
  destinationCity?: string;
  destinationTerminal?: string;
  aircraft?: string;
  duration?: string;
  passengers: Array<{
    passengerId: string;
    cabin?: string;
    seat?: string;
    baggage: Array<{ type: 'checked' | 'carry_on'; quantity: number }>;
  }>;
}
export interface FlightTicket {
  provider: string;
  mode: 'sandbox' | 'production';
  orderId: string;
  status: 'CONFIRMED' | 'PENDING' | 'FAILED' | 'UNKNOWN';
  bookingReference?: string;
  issuedAt?: string;
  airline: { iata?: string; name: string; logoUrl?: string; conditionsOfCarriageUrl?: string };
  passengers: Array<{ id: string; type?: string; title?: string; givenName: string; familyName: string; ticketNumbers: string[] }>;
  slices: Array<{ fareBrand?: string; segments: TicketSegment[] }>;
  conditions: { refundBeforeDeparture?: TicketCondition; changeBeforeDeparture?: TicketCondition };
  /** Priced by the BFF (commercial-contract §6.13) — `null` without a price card (D17). The
   *  sheet prints no amount: these never reach the screen. */
  price: { total: TicketMoney | null; base?: TicketMoney | null; tax?: TicketMoney | null };
  /** D17 — `false`: the price card was unavailable and every amount above is `null`. */
  priced?: boolean;
}
export interface TicketDelivery {
  channel: 'EMAIL' | 'SMS';
  trigger: 'auto' | 'resend';
  status: 'PENDING' | 'SENT' | 'FAILED' | 'SKIPPED';
  /** Masked by notifications (`pa***@example.com`, `•••0123`). */
  destination?: string;
  code?: string;
  provider?: string;
  simulated: boolean;
  mode?: 'sandbox' | 'production';
  at: string;
}

const options = { timeout: 65_000, retry: 0 } as const;
const codes = new WeakMap<Response, string>();
const safe = (v: unknown): v is string => typeof v === 'string' && /^[A-Z][A-Z0-9_]{0,80}$/.test(v);
// Capture a safe code before the shared client's beforeError consumes the body. Built on
// first use: the panel that imports this is mounted by screens whose tests stub `http`.
let extended: typeof http | undefined;
const client = () => (extended ??= http.extend({
  hooks: {
    afterResponse: [
      async (_req, _opts, response) => {
        if (!response.ok) {
          try {
            const body = (await response.clone().json()) as { code?: unknown };
            if (safe(body.code)) codes.set(response, body.code);
          } catch {
            /* Unknown failure stays unknown. */
          }
        }
        return response;
      },
    ],
  },
}));
const base = (kind: FlightTicketKind, id: string) => `v1/flight-tickets/${kind}/${encodeURIComponent(id)}`;

export const flightTicketApi = {
  ticket: (kind: FlightTicketKind, id: string) => client().get(base(kind, id), options).json<FlightTicket>(),
  deliveries: (kind: FlightTicketKind, id: string) =>
    client().get(`${base(kind, id)}/deliveries`, options).json<{ deliveries: TicketDelivery[] }>(),
  resend: (kind: FlightTicketKind, id: string, key: string) =>
    client()
      .post(`${base(kind, id)}/resend`, { ...options, json: {}, headers: { 'Idempotency-Key': key } })
      .json<{ sent: TicketDelivery[]; deliveries: TicketDelivery[] }>(),
};

export const flightTicketKeys = {
  ticket: (kind: FlightTicketKind, id: string) => ['flight-ticket', kind, id] as const,
  deliveries: (kind: FlightTicketKind, id: string) => ['flight-ticket', kind, id, 'deliveries'] as const,
};

/** The upstream's safe error code, or a generic one — never upstream text. */
export async function flightTicketError(error: unknown): Promise<string> {
  if (error instanceof HTTPError) {
    const known = codes.get(error.response);
    if (known) return known;
    try {
      const body = (await error.response.clone().json()) as { code?: unknown };
      if (safe(body.code)) return body.code;
    } catch {
      /* Safe code only. */
    }
  }
  return 'TICKET_UNAVAILABLE';
}
